import { IconProp, library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { ActionIcon, Box, Button, Card, ComboboxItem, ComboboxLikeRenderOptionInput, Group, Primitive, Stack, Text, Title, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { GridColDef } from "@mui/x-data-grid";
import { IconClock, IconTrash } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Control, SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

import { Input } from "@/components/_Input";
import { DataGridBase } from "@/components/DataGridBase";
import { useSelectedCompany } from "@/features/dashboard/configuracoes/_context/SelectedCompanyContext";
import { useIsMobile } from "@/hooks";
import { useDeleteDelayMutation, useGetSettingsByCompanyQuery, useUpdateSettingsMutation } from "@/store";

export const Route = createFileRoute("/_private/dashboard/_settings/configuracoes/avisos")({
  component: RouteComponent
});

const schema = z.object({
  id: z.number().nullable(),
  reason: z.string().min(2, "O motivo deve ter ao menos 2 caracteres").max(100, "O motivo deve ter no máximo 100 caracteres"),
  icon: z
    .string({ error: ({ code }) => (code === "invalid_type" ? "Selecione um ícone" : undefined) })
    .min(1, "Selecione um ícone")
    .max(50, "Ícone inválido"),
  time: z.string().regex(/^([0-1]\d|2[0-3]):([0-5]\d)$/, "Deve ser um horário válido no formato HH:MM")
});
type DelayForm = z.infer<typeof schema>;

// Lista inicial de ícones mais comuns para evitar travamento (movida para fora do componente)
const COMMON_ICONS = [
  "car",
  "clock",
  "warning",
  "tools",
  "exclamation-triangle",
  "road",
  "truck",
  "gas-pump",
  "wrench",
  "battery-empty",
  "temperature-high",
  "rain",
  "snow",
  "wind",
  "sun",
  "cloud",
  "stopwatch",
  "calendar",
  "map",
  "location-dot",
  "phone",
  "envelope",
  "user",
  "users",
  "building",
  "home",
  "star",
  "heart",
  "thumbs-up",
  "thumbs-down",
  "check",
  "times",
  "plus",
  "minus",
  "edit",
  "trash",
  "save",
  "print",
  "download",
  "upload",
  "search"
];

// Componente isolado para seleção de ícones
interface IconSelectProps {
  control: Control<DelayForm>;
  name: "icon";
  label: string;
  placeholder: string;
}

const IconSelect = memo(({ control, name, label, placeholder }: IconSelectProps) => {
  const [awesomeIcons, setAwessomeIcons] = useState<{ value: string; label: string }[]>([]);
  const [allIcons, setAllIcons] = useState<{ value: string; label: string }[]>([]);
  const [fasIcons, setFasIcons] = useState<Record<string, unknown> | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);

  // Memoiza os ícones comuns iniciais para evitar re-criação
  const initialIcons = useMemo(() => COMMON_ICONS.map((icon) => ({ value: icon, label: icon })), []);

  // Memoiza a função de busca para evitar re-renderizações
  const handleIconSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Memoiza a função de renderização de opções para evitar re-renderizações
  const renderIconOption = useCallback(
    ({ option }: ComboboxLikeRenderOptionInput<ComboboxItem<Primitive>>) => {
      try {
        // Converte o nome do ícone de volta para o formato FontAwesome (faCamelCase)
        if (typeof option.value !== "string") return;

        const faIconName =
          "fa" +
          option.value
            .split("-")
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join("");

        // Verifica se o ícone existe
        const fasObj = fasIcons as Record<string, unknown> | null;
        if (fasObj && fasObj[faIconName]) {
          return (
            <Group gap="xs">
              <FontAwesomeIcon icon={["fas", option.value] as IconProp} />
              <Text>{option.label}</Text>
            </Group>
          );
        }

        // Se o ícone não existir, renderiza apenas o texto
        return (
          <Group gap="xs">
            <Text>{option.label}</Text>
          </Group>
        );
      } catch (_error) {
        // Se o ícone não existir, renderiza apenas o texto
        return (
          <Group gap="xs">
            <Text>{option.label}</Text>
          </Group>
        );
      }
    },
    [fasIcons]
  );

  useEffect(() => {
    // Carrega apenas ícones comuns inicialmente
    setAwessomeIcons(initialIcons);

    // Importa pacote completo de ícones de forma dinâmica para evitar pesar o bundle inicial
    let cancelled = false;
    import("@fortawesome/free-solid-svg-icons").then((mod) => {
      if (cancelled) return;
      const fasModule = (mod as unknown as { fas: Record<string, unknown> }).fas;
      setFasIcons(fasModule);
      library.add((mod as any).fas);

      const allIconsList = Object.keys(fasModule)
        .map((icon) => {
          const iconName = icon.startsWith("fa")
            ? icon
                .slice(2)
                .replace(/([A-Z])/g, "-$1")
                .toLowerCase()
                .replace(/^-/, "")
            : icon;

          return { value: iconName, label: iconName };
        })
        .filter((iconOption) => {
          try {
            const faName =
              "fa" +
              iconOption.value
                .split("-")
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join("");
            return Boolean((fasModule as Record<string, unknown>)[faName]);
          } catch {
            return false;
          }
        });
      setAllIcons(allIconsList);
    });

    return () => {
      cancelled = true;
    };
  }, [initialIcons]);

  // Efeito para busca com debounce - otimizado para evitar re-renderizações
  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      // Se não há busca, mostra apenas ícones comuns
      setAwessomeIcons(initialIcons);
      return;
    }

    // Busca nos ícones carregados (primeiro nos comuns, depois em todos)
    const searchLower = debouncedSearchQuery.toLowerCase();
    const iconsToSearch = allIcons.length > 0 ? allIcons : initialIcons;

    const filtered = iconsToSearch.filter((icon) => icon.value.toLowerCase().includes(searchLower));

    setAwessomeIcons(filtered);
  }, [debouncedSearchQuery, allIcons, initialIcons]);

  return (
    <Input.Select control={control} name={name} label={label} placeholder={placeholder} data={awesomeIcons} onSearchChange={handleIconSearch} renderOption={renderIconOption} />
  );
});
IconSelect.displayName = "IconSelect";

function RouteComponent() {
  const isMobile = useIsMobile();
  const { companyId } = useSelectedCompany();
  const { data, isFetching, isSuccess } = useGetSettingsByCompanyQuery(
    { id: Number(companyId) },
    {
      skip: !companyId || !Number(companyId)
    }
  );
  const [saveSettings, { isSuccess: isSaveSuccess }] = useUpdateSettingsMutation();
  const [deleteDelay] = useDeleteDelayMutation();

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, isValid }
  } = useForm<DelayForm>({
    defaultValues: {
      id: null,
      reason: "",
      icon: "",
      time: ""
    },
    resolver: zodResolver(schema),
    mode: "all"
  });

  const onSubmit: SubmitHandler<DelayForm> = async (values) => {
    if (!isSuccess) return;

    await saveSettings({ ...data, company: Number(companyId), delay: [...data.delay, values] });
  };

  const handleDeleteDelay = async (id: number) => {
    await deleteDelay({ id });
  };

  const columns: GridColDef<DelayForm>[] = [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      type: "number",
      width: 80
    },
    {
      field: "reason",
      headerName: "Motivo",
      display: "flex",
      flex: 1,
      type: "custom",
      minWidth: 200,
      renderCell: (params) => {
        return (
          <Box className="flex items-center gap-2">
            <FontAwesomeIcon icon={["fas", params.row.icon] as IconProp} />
            <Text>{params.row.reason}</Text>
          </Box>
        );
      }
    },
    {
      field: "time",
      headerName: "Tempo de atraso",
      align: "center",
      headerAlign: "center",
      type: "string",
      width: 150
    },
    {
      field: "action",
      headerName: "Ações",
      type: "actions",
      width: 110,
      renderCell: (params) => (
        <Tooltip label="Deletar motivo de atraso">
          <ActionIcon color="red" onClick={() => handleDeleteDelay(params.row.id!)}>
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      )
    }
  ];

  useEffect(() => {
    if (isSaveSuccess) {
      reset();
    }
  }, [isSaveSuccess]);

  return (
    <Stack gap="xl">
      <Group justify="space-between" align="center">
        <Title order={2}>Avisos</Title>
      </Group>

      {/* Formulário para adicionar novo motivo */}
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Title order={4}>Adicionar Novo Motivo de Atraso</Title>

              <Button type="submit" color="blue" disabled={!isValid || !isSuccess} loading={isSubmitting || isFetching} leftSection={<IconClock size={16} />} fullWidth={isMobile}>
                Adicionar
              </Button>
            </Group>

            <div className="grid grid-cols-1 place-content-start gap-4 sm:grid-cols-2 md:grid-cols-3">
              <Input.Text control={control} name="reason" label="Motivo" placeholder="Digite o motivo do atraso" />

              <IconSelect control={control} name="icon" label="Ícone" placeholder="Selecione um ícone" />

              <Input.Text control={control} name="time" label="Tempo de Atraso" type="time" />
            </div>
          </Stack>
        </form>
      </Card>

      {/* Tabela de motivos de atraso */}
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <Stack gap="md">
          <Title order={4}>Motivos Cadastrados</Title>

          <DataGridBase rows={data?.delay || []} columns={columns} loading={isFetching} />
        </Stack>
      </Card>
    </Stack>
  );
}
